import { useShopI18n } from '../i18n'

type ErrorCodePageProps = {
  code: number
  message?: string
  onGoCatalog: () => void
  onContactSupport?: () => void
  onGoHome: () => void
  onGoBack: () => void
  onReload: () => void
}

type ErrorDetails = {
  eyebrowKey:
    | 'error.401.eyebrow'
    | 'error.403.eyebrow'
    | 'error.404.eyebrow'
    | 'error.418.eyebrow'
    | 'error.500.eyebrow'
    | 'error.503.eyebrow'
  titleKey:
    | 'error.401.title'
    | 'error.403.title'
    | 'error.404.title'
    | 'error.418.title'
    | 'error.500.title'
    | 'error.503.title'
  descriptionKey:
    | 'error.401.description'
    | 'error.403.description'
    | 'error.404.description'
    | 'error.418.description'
    | 'error.500.description'
    | 'error.503.description'
}

const ERROR_LIBRARY: Record<number, ErrorDetails> = {
  401: { eyebrowKey: 'error.401.eyebrow', titleKey: 'error.401.title', descriptionKey: 'error.401.description' },
  403: { eyebrowKey: 'error.403.eyebrow', titleKey: 'error.403.title', descriptionKey: 'error.403.description' },
  404: { eyebrowKey: 'error.404.eyebrow', titleKey: 'error.404.title', descriptionKey: 'error.404.description' },
  418: { eyebrowKey: 'error.418.eyebrow', titleKey: 'error.418.title', descriptionKey: 'error.418.description' },
  500: { eyebrowKey: 'error.500.eyebrow', titleKey: 'error.500.title', descriptionKey: 'error.500.description' },
  503: { eyebrowKey: 'error.503.eyebrow', titleKey: 'error.503.title', descriptionKey: 'error.503.description' },
}

const clampErrorCode = (value: number) => {
  if (!Number.isFinite(value)) return 500
  if (value < 100 || value > 599) return 500
  return Math.trunc(value)
}

function ErrorCodePage({
  code,
  message,
  onGoCatalog,
  onGoHome,
  onGoBack,
  onReload,
}: ErrorCodePageProps) {
  const { t } = useShopI18n()
  const normalizedCode = clampErrorCode(code)
  const isNotFound = normalizedCode === 404
  const isPremium500 = normalizedCode === 500
  const details = ERROR_LIBRARY[normalizedCode]
  const eyebrow = details ? t(details.eyebrowKey) : t('error.unknownEyebrow', { code: normalizedCode })
  const title = details ? t(details.titleKey) : t('error.unknownTitle')
  const description = details ? t(details.descriptionKey) : t('error.unknownDescription')

  if (isPremium500) {
    return (
      <section
        className="relative isolate min-h-[calc(100vh-12rem)] px-4 py-9 sm:px-6 sm:py-12"
        style={{
          background:
            'radial-gradient(circle at 8% 10%, color-mix(in srgb, var(--theme-color-accent, #0ea5e9) 12%, transparent), transparent 34%), radial-gradient(circle at 90% 0%, color-mix(in srgb, var(--theme-color-primary, #059669) 14%, transparent), transparent 30%), linear-gradient(180deg, color-mix(in srgb, var(--theme-color-background, #f8fafc) 92%, #ffffff) 0%, color-mix(in srgb, var(--theme-color-background, #f8fafc) 86%, #e2e8f0) 100%)',
        }}
      >
        <div className="mx-auto flex w-full max-w-[620px] items-center justify-center">
          <article
            className="shop-surface relative w-full overflow-hidden rounded-3xl px-6 py-8 text-center shadow-[0_22px_56px_-40px_rgba(15,23,42,0.5)] sm:px-9 sm:py-10"
            aria-labelledby="shop-error-500-title"
          >
            <div className="pointer-events-none absolute -right-12 -top-12 h-28 w-28 rounded-full bg-cyan-100/35 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-12 h-28 w-28 rounded-full bg-emerald-100/35 blur-2xl" />

            <div className="space-y-7">
              <div className="space-y-4">
                <p className="inline-flex rounded-full border border-slate-200/80 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                  {eyebrow}
                </p>
                <div className="mx-auto inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200/80 bg-white/85 text-slate-600">
                  <svg viewBox="0 0 24 24" className="h-4.5 w-4.5" fill="none" aria-hidden="true">
                    <path d="M12 7v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="15.5" r="0.9" fill="currentColor" />
                    <rect x="4" y="3.5" width="16" height="17" rx="4" stroke="currentColor" strokeWidth="1.4" />
                  </svg>
                </div>
                <h1 id="shop-error-500-title" className="text-3xl font-black tracking-tight text-slate-900 sm:text-[2.15rem]">
                  {title}
                </h1>
                <p className="mx-auto max-w-[46ch] text-base leading-relaxed text-slate-600 sm:text-[1.05rem]">
                  {description}
                </p>
              </div>

              <div className="space-y-3.5">
                <button
                  type="button"
                  onClick={onReload}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:w-auto sm:min-w-40"
                >
                  {t('common.retry')}
                </button>
                <button
                  type="button"
                  onClick={onGoHome}
                  className="inline-flex items-center justify-center text-sm font-medium text-slate-600 underline-offset-4 transition hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                >
                  {t('common.goHome')}
                </button>
              </div>

              <p className="text-xs text-slate-500/90">
                {t('error.500.referenceLabel')}: ERR-{normalizedCode}
              </p>
            </div>
          </article>
        </div>
      </section>
    )
  }

  return (
    <div
      className="px-4 py-4 text-slate-900 sm:px-6 sm:py-5"
      style={{
        background:
          'radial-gradient(circle at 18% 12%, rgba(14,165,233,0.10), transparent 36%), radial-gradient(circle at 88% 6%, rgba(16,185,129,0.10), transparent 28%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
      }}
    >
      <div className={`w-full ${isNotFound ? 'mx-auto max-w-3xl' : ''}`}>
        <section
          className={`relative overflow-hidden border bg-white shadow-[0_30px_70px_-45px_rgba(15,23,42,0.55)] ${
            isNotFound ? 'border-slate-200/80 bg-white/95' : 'rounded-2xl border-slate-200'
          }`}
          style={isNotFound ? { borderRadius: '40px 56px 44px 52px' } : undefined}
        >
          {isNotFound && (
            <>
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-emerald-100/70 blur-2xl" />
              <div className="pointer-events-none absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-sky-100/70 blur-2xl" />
            </>
          )}
          <div className={`p-5 sm:p-6 lg:p-7 ${isNotFound ? 'text-center' : ''}`}>
            {!isNotFound && (
              <p className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
                {eyebrow}
              </p>
            )}

            <div className="mt-4 space-y-2.5">
              <p className="text-5xl font-black leading-none text-slate-900 sm:text-6xl">{normalizedCode}</p>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
              <p className={`max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg ${isNotFound ? 'mx-auto' : ''}`}>
                {message || description}
              </p>
            </div>

            <div className={`mt-5 flex flex-wrap gap-2.5 ${isNotFound ? 'justify-center' : ''}`}>
              <button
                type="button"
                onClick={onGoHome}
                className="rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                {t('common.goHome')}
              </button>
              <button
                type="button"
                onClick={onGoBack}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:border-slate-400"
              >
                {t('common.goBack')}
              </button>
              <button
                type="button"
                onClick={onReload}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:border-slate-400"
              >
                {t('common.reload')}
              </button>
              {!isNotFound ? (
                <button
                  type="button"
                  onClick={onGoCatalog}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:border-slate-400"
                >
                  {t('common.goCatalog')}
                </button>
              ) : null}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default ErrorCodePage
